/**
 * OrdersPage Component
 */

import { requestFetchCountries, requestFetchStatesForCountry } from '../../../../redux/actions/countries';
import { getCountryFromCode } from '../../../../redux/helpers/countries';
import { OrderPageFragment } from '../../../../relay/order';
import { emptyPromiseActionPayload } from '../../../../utils/promise';
import { linkWithCurrentQueryString } from '../../../../utils/router';
import './styles.scss';

import * as classNames from 'classnames';
import { RouteMatch } from 'found';
import * as nextTick from 'next-tick';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';

import Page from '../../../../containers/Page';
import Button from '../../../atoms/buttons/Button';
import NProgress from '../../../atoms/feedback/NProgress';
import { IconPrint } from '../../../atoms/images/Icon';
import Heading from '../../../atoms/text/Heading';
import PageHeader from '../../../molecules/blocks/PageHeader';
import NotAuthorizedPage from '../NotAuthorized';
import OrderList from './OrderList';
import OrderView from './OrderView';

// Page Path
export const OrdersPagePath = '/my/orders';
export const OrderPagePath = (id: string) => `/my/orders/${id}`;

// Page Props
export type OrderDocumentType = 'invoice' | 'license' | 'music';
interface IOwnStateProps {
  activeOrderExists: boolean;
  hasOrders: boolean;
  trackDetailsSidebarIsVisible: boolean;
  userIsLoggedIn: boolean;
  getCountry(countryCode: string): ICountry | undefined;
}

interface IOwnProps extends RouteMatch {
  viewer: GQL.IViewer;
}

type OrdersPageBaseProps = IOwnProps;
export type OrdersPageProps = OrdersPageBaseProps & IOwnStateProps & IDispatchProps;

interface IOwnState {
  activeOrder: string;
  currentDocument?: OrderDocumentType;
  defaultDocument: OrderDocumentType | undefined;
  fetching: boolean;
  settingsPanelOpen: boolean;
}

const classes = new BEM('orders');

class OrdersPage extends React.Component<OrdersPageProps, IOwnState> {
  orders: GQL.IOrder[];
  fetchingTimer: number;

  constructor(props: OrdersPageProps) {
    super(props);

    this.state = {
      activeOrder: props.routeParams.invoice_number || '',
      defaultDocument: props.location.query.view as OrderDocumentType || undefined,
      fetching: props.hasOrders,
      settingsPanelOpen: false,
    };

    if (this.props.hasOrders) {
      this.orders = this.props.viewer.my_orders!;
    }
  }

  componentDidMount() {
    if (this.props.hasOrders) {
      this.props.dispatch(requestFetchCountries({
        promise: {
          resolve: () => {
            this.fetchActiveOrderAddressesStates();
            this.initializeView();
          },
          reject: () => null,
        },
      }));
    }
  }

  initializeView() {
    if (!this.state.activeOrder) {
      const defaultInvoiceNumber = `${this.orders[0]!.invoice_number!}`;
      this.props.router.replace(linkWithCurrentQueryString(OrderPagePath(defaultInvoiceNumber)));
    } else {
      this.setState({ fetching: false });
    }
  }

  componentDidUpdate(prevProps: OrdersPageProps) {
    if (this.hasSwitchedOrder(prevProps)) {
      this.setState({
        activeOrder: this.props.routeParams.invoice_number,
      }, () => {
        this.fetchActiveOrderAddressesStates();
        this.fetchingTimer = window.setTimeout(() => {
          this.setState({ fetching: false });
        }, 1000);
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.fetchingTimer);
  }

  onSelectOrder = (activeOrder: string) => {
    if (activeOrder !== this.state.activeOrder) {
      this.setState({ fetching: true });
    }
  }

  render() {
    const {
      currentDocument,
      defaultDocument,
      fetching,
      settingsPanelOpen,
    } = this.state;

    const {
      activeOrderExists,
      hasOrders,
      trackDetailsSidebarIsVisible,
      userIsLoggedIn,
    } = this.props;

    const extraClasses = classNames(
      settingsPanelOpen && 'settings-open',
      trackDetailsSidebarIsVisible && 'details-open',
      !userIsLoggedIn && 'not-authorized',
      userIsLoggedIn && (!activeOrderExists || !hasOrders) && 'order-not-found',
    );

    if (!userIsLoggedIn) {
      return <NotAuthorizedPage message="You must be logged in to access your orders." />;
    }

    return (
      <Page id="orders" className={extraClasses} noIndex>
        <PageHeader
          title="My Orders"
          actions={(
            !!activeOrderExists && <Button
              label="Print or Save as PDF"
              disabled={fetching || currentDocument === 'music'}
              colorScheme="transparent"
              icon={<IconPrint />}
              onClick={() => nextTick(window.print)}
            />
          )}
        />
        <NProgress active={fetching} {...classes('loading-progress') } />
        <div {...classes('content-wrapper') }>
          {hasOrders ? (
            <>
              <OrderList orders={this.orders} onSelectOrder={this.onSelectOrder} />
              {activeOrderExists ? (
                <OrderView
                  defaultDocument={defaultDocument}
                  isFetching={fetching}
                  order={this.getActiveOrder()}
                  onSwitchDocument={(document: OrderDocumentType) => this.setState({ currentDocument: document })}
                />
              ) : !fetching ?
                <Heading is="h2" textColor="bw6" {...classes('error-message') }>Order Not Found</Heading>
                : null}
            </>
          ) : (
            <Heading
              is="h2"
              textColor="bw6"
              {...classes('error-message', 'no-order') }
            >You don't have any orders.
            </Heading>
          )}
        </div>
      </Page>
    );
  }

  private getActiveOrder(props = this.props) {
    return props.viewer.order!;
  }

  private hasSwitchedOrder(prevProps: OrdersPageProps) {
    return prevProps.routeParams.invoice_number !== this.props.routeParams.invoice_number;
  }

  private fetchActiveOrderAddressesStates() {
    if (this.state.activeOrder) {
      const activeOrder = this.getActiveOrder();
      if (activeOrder) {
        if (activeOrder.licensee_address) {
          this.props.dispatch(requestFetchStatesForCountry({
            country: this.props.getCountry(activeOrder.licensee_address.country_code)!,
            ...emptyPromiseActionPayload,
          }));
        }

        if (activeOrder.billing_address) {
          this.props.dispatch(requestFetchStatesForCountry({
            country: this.props.getCountry(activeOrder.billing_address!.country_code)!,
            ...emptyPromiseActionPayload,
          }));
        }
      }
    }
  }
}

const ConnectedOrdersPage = connect<IOwnStateProps, IDispatchProps, OrdersPageBaseProps , IStoreState>(
  (state, ownProps) => ({
    activeOrderExists: !!ownProps.routeParams.invoice_number && !!ownProps.viewer.order,
    getCountry: (countryCode) => getCountryFromCode(countryCode)(state),
    hasOrders: !!ownProps.viewer.my_orders && !!ownProps.viewer.my_orders.length,
    trackDetailsSidebarIsVisible: !!state.ui.trackDetails && state.ui.trackDetails.visible,
    userIsLoggedIn: state.user.isLoggedIn,
  }),
  (dispatch) => ({ dispatch }),
)(OrdersPage);

const RelayOrdersPage = createFragmentContainer(
  ConnectedOrdersPage,
  OrderPageFragment,
);

export default RelayOrdersPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/account/Orders/index.tsx